import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout, { DateInput, SearchableSelect } from './Layout';
import { formatDate, getApiUrl, getTodayIso } from './config';

function Receipt() {
    const [customers, setCustomers] = useState([]);
    const [receipt, setReceipt] = useState({ customer_id: '', date: getTodayIso(), note: '' });
    const [payments, setPayments] = useState([{ payment_mode: 'Cash', amount: 0 }]);
    const [prevBalance, setPrevBalance] = useState(0);
    const [editingId, setEditingId] = useState(null);
    const [editingOriginalTotal, setEditingOriginalTotal] = useState(0);
    const [editingOriginalCustomerId, setEditingOriginalCustomerId] = useState("");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const formatWhatsAppNumber = (value) => {
        const digits = String(value || "").replace(/\D/g, "").replace(/^0+/, "");
        if (!digits) return "";
        if (digits.length === 10) return `91${digits}`;
        return digits;
    };

    const openWhatsApp = (phone, message) => {
        const digits = formatWhatsAppNumber(phone);
        if (!digits) {
            alert("Customer WhatsApp number not found");
            return;
        }
        const url = `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const buildReceiptWhatsAppMessage = ({ customerName, date, prev, remaining, cashPaid, upiPaid }) => {
        const dateText = formatDate(date);
        const prevText = `₹ ${Number(prev || 0).toFixed(2)}`;
        const remainingText = `₹ ${Number(remaining || 0).toFixed(2)}`;
        const cashText = `₹ ${Number(cashPaid || 0).toFixed(2)}`;
        const upiText = `₹ ${Number(upiPaid || 0).toFixed(2)}`;
        return [
            `Payment Receipt`,
            `Date: ${dateText}`,
            `Customer: ${customerName || "-"}`,
            `Previous Balance: ${prevText}`,
            `Payment Cash: ${cashText}`,
            `Payment UPI: ${upiText}`,
            `Remaining Amount: ${remainingText}`
        ].filter(Boolean).join("\n");
    };

    useEffect(() => {
        fetchCustomers();
    }, []);

    useEffect(() => {
        if (!receipt.customer_id) {
            setPrevBalance(0);
            return;
        }
        const fetchBalance = async () => {
            try {
                const res = await axios.get(`${getApiUrl()}/customers/${receipt.customer_id}/balance`);
                setPrevBalance(Number(res.data.balance) || 0);
            } catch (err) {
                console.error(err);
                setPrevBalance(0);
            }
        };
        fetchBalance();
    }, [receipt.customer_id]);

    const fetchCustomers = async () => {
        try {
            const res = await axios.get(`${getApiUrl()}/customers`);
            setCustomers(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchReceipts = async () => {
        try {
            setLoading(true);
            const params = {};
            if (from) params.from = from;
            if (to) params.to = to;
            params.include_no_date = 1;
            const res = await axios.get(`${getApiUrl()}/receipts`, { params });
            setRows(res.data || []);
        } catch (err) {
            console.error(err);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReceipts();
    }, [from, to]);

    const resetForm = () => {
        setReceipt({ customer_id: '', date: getTodayIso(), note: '' });
        setPayments([{ payment_mode: 'Cash', amount: 0 }]);
        setPrevBalance(0);
        setEditingId(null);
        setEditingOriginalTotal(0);
        setEditingOriginalCustomerId("");
    };

    const handleReceipt = async (e) => {
        e.preventDefault();
        const total = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
        if (!receipt.customer_id) return alert("Select Customer");
        if (total === 0) return alert("Enter payment amount");
        try {
            const normalizedPayments = payments
                .map(p => ({ payment_mode: p.payment_mode, amount: parseFloat(p.amount) || 0 }))
                .filter(p => p.payment_mode && p.amount !== 0);

            const cashPaid = normalizedPayments
                .filter(p => String(p.payment_mode || "").toLowerCase().includes("cash"))
                .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
            const upiPaid = normalizedPayments
                .filter(p => String(p.payment_mode || "").toLowerCase().includes("upi"))
                .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
            const selectedCustomer = customers.find(c => String(c.id) === String(receipt.customer_id));
            const customerPhone = selectedCustomer?.contact || selectedCustomer?.customer_contact || "";
            const customerName = selectedCustomer?.name || "";
            const receiptDate = receipt.date;

            if (editingId) {
                await axios.put(`${getApiUrl()}/receipts/${editingId}`, { ...receipt, payments: normalizedPayments });
                alert('Receipt Updated');
            } else {
                await axios.post(`${getApiUrl()}/receipts`, { ...receipt, payments: normalizedPayments });
                alert('Receipt Recorded');
            }

            const fallbackPrevBefore = (editingId && String(receipt.customer_id) === String(editingOriginalCustomerId))
                ? ((Number(prevBalance) || 0) + (Number(editingOriginalTotal) || 0))
                : (Number(prevBalance) || 0);
            let balanceAfter = fallbackPrevBefore - total;
            try {
                const balRes = await axios.get(`${getApiUrl()}/customers/${receipt.customer_id}/balance`);
                balanceAfter = Number(balRes.data.balance) || 0;
            } catch (err) {
                console.error(err);
            }
            const prevBefore = balanceAfter + total;

            if (window.confirm("Send WhatsApp message?")) {
                const msg = buildReceiptWhatsAppMessage({
                    customerName,
                    date: receiptDate,
                    prev: prevBefore,
                    remaining: balanceAfter,
                    cashPaid,
                    upiPaid
                });
                openWhatsApp(customerPhone, msg);
            }

            resetForm();
            fetchReceipts();
        } catch (err) {
            console.error(err);
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.sqlMessage ||
                err?.response?.data?.code ||
                "Failed to save receipt";
            alert(message);
        }
    };

    const handleEdit = async (id) => {
        try {
            const res = await axios.get(`${getApiUrl()}/receipts/${id}`);
            const data = res.data || {};
            const invoiceDate = String(data.date || "").slice(0, 10) || getTodayIso();
            setEditingId(id);
            setReceipt({
                customer_id: String(data.customer_id || ""),
                date: invoiceDate,
                note: data.note || ""
            });
            setEditingOriginalCustomerId(String(data.customer_id || ""));
            const nextPayments = Array.isArray(data.payments) && data.payments.length > 0
                ? data.payments.map(p => ({ payment_mode: p.payment_mode || "Cash", amount: Number(p.amount) || 0 }))
                : [{ payment_mode: "Cash", amount: Number(data.amount) || 0 }];
            setPayments(nextPayments);
            const originalTotal = nextPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
            setEditingOriginalTotal(originalTotal);
        } catch (err) {
            console.error(err);
            alert("Failed to load receipt");
        }
    };

    const handleDelete = async (id) => {
        const ok = window.confirm("Delete this receipt?");
        if (!ok) return;
        try {
            await axios.delete(`${getApiUrl()}/receipts/${id}`);
            if (String(editingId) === String(id)) resetForm();
            fetchReceipts();
        } catch (err) {
            console.error(err);
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.sqlMessage ||
                err?.response?.data?.code ||
                "Failed to delete receipt";
            alert(message);
        }
    };

    const handleWhatsAppRow = async (row) => {
        try {
            const res = await axios.get(`${getApiUrl()}/receipts/${row.id}`);
            const data = res.data || {};
            const cashPaid = Number(data.cash_amount || 0);
            const upiPaid = Number(data.upi_amount || 0);
            const prev = Number(data.prev_balance || 0);
            const remaining = Number(data.balance_after || 0);
            const msg = buildReceiptWhatsAppMessage({
                customerName: data.customer_name || row.customer_name || "",
                date: data.date || row.date,
                prev,
                remaining,
                cashPaid,
                upiPaid
            });
            openWhatsApp(data.customer_contact || row.customer_contact || "", msg);
        } catch (err) {
            console.error(err);
            alert("Failed to send WhatsApp");
        }
    };

    const totalEntered = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const prevForForm = (editingId && String(receipt.customer_id) === String(editingOriginalCustomerId))
        ? ((Number(prevBalance) || 0) + (Number(editingOriginalTotal) || 0))
        : (Number(prevBalance) || 0);
    const balanceAfterForm = prevForForm - totalEntered;

    return (
        <Layout>
            <div className="container mt-4">
                <h2>Payment Receipt</h2>
                <form onSubmit={handleReceipt} className="card p-4">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label">Customer</label>
                            <SearchableSelect
                                value={receipt.customer_id}
                                options={customers.map(c => ({ value: String(c.id), label: c.name || '' }))}
                                placeholder="Type customer name..."
                                required
                                onChange={(nextId) => setReceipt({ ...receipt, customer_id: nextId })}
                                allowClear
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Date</label>
                            <DateInput value={receipt.date} onChange={(v) => setReceipt({ ...receipt, date: v })} required />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Total</label>
                            <input
                                type="number"
                                className="form-control"
                                value={totalEntered}
                                readOnly
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Previous Balance</label>
                            <input type="number" className="form-control" value={prevForForm} readOnly />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Remaining Amount</label>
                            <input
                                type="number"
                                className="form-control"
                                value={balanceAfterForm}
                                readOnly
                            />
                        </div>
                        <div className="col-12">
                            <label className="form-label">Note</label>
                            <textarea className="form-control" value={receipt.note} onChange={e => setReceipt({...receipt, note: e.target.value})} onFocus={(e) => e.target.select()} />
                        </div>
                        <div className="col-12">
                            <label className="form-label">Payments</label>
                            <div className="table-responsive">
                                <table className="table table-bordered">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Mode</th>
                                            <th>Amount</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments.map((p, idx) => (
                                            <tr key={idx}>
                                                <td>
                                                    <select
                                                        className="form-select"
                                                        value={p.payment_mode}
                                                        onChange={e => {
                                                            const next = [...payments];
                                                            next[idx] = { ...next[idx], payment_mode: e.target.value };
                                                            setPayments(next);
                                                        }}
                                                    >
                                                        <option value="Cash">Cash</option>
                                                        <option value="UPI">UPI</option>
                                                        <option value="Bank Transfer">Bank Transfer</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={p.amount}
                                                        onChange={e => {
                                                            const next = [...payments];
                                                            next[idx] = { ...next[idx], amount: e.target.value };
                                                            setPayments(next);
                                                        }}
                                                        onFocus={(e) => e.target.select()}
                                                    />
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => setPayments(payments.filter((_, i) => i !== idx))}
                                                        disabled={payments.length === 1}
                                                    >
                                                        Remove
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setPayments([...payments, { payment_mode: 'Cash', amount: 0 }])}
                            >
                                Add Mode
                            </button>
                        </div>
                        <div className="col-12 mt-3 d-grid d-md-block">
                            <button type="submit" className="btn btn-primary btn-lg">
                                {editingId ? "Update Receipt" : "Save Receipt"}
                            </button>
                            {editingId ? (
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-lg ms-2"
                                    onClick={resetForm}
                                >
                                    Cancel Edit
                                </button>
                            ) : null}
                        </div>
                    </div>
                </form>

                <div className="card p-3 mt-4">
                    <div className="d-flex flex-wrap align-items-end gap-3">
                        <div>
                            <label className="form-label">From</label>
                            <DateInput value={from} onChange={setFrom} />
                        </div>
                        <div>
                            <label className="form-label">To</label>
                            <DateInput value={to} onChange={setTo} />
                        </div>
                        <div className="ms-auto">
                            <button type="button" className="btn btn-outline-primary" onClick={fetchReceipts} disabled={loading}>
                                {loading ? "Loading..." : "Refresh"}
                            </button>
                        </div>
                    </div>

                    <div className="table-responsive mt-3">
                        <table className="table table-bordered">
                            <thead className="table-dark">
                                <tr>
                                    <th>Date</th>
                                    <th>Customer</th>
                                    <th>Total</th>
                                    <th>Mode</th>
                                    <th>Note</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => (
                                    <tr key={r.id}>
                                        <td>{formatDate(r.date)}</td>
                                        <td>{r.customer_name || "-"}</td>
                                        <td>{Number(r.amount) || 0}</td>
                                        <td>{r.payment_mode || "-"}</td>
                                        <td style={{ minWidth: "200px" }}>{r.note || "-"}</td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            <button type="button" className="btn btn-sm btn-outline-success" onClick={() => handleWhatsAppRow(r)}>
                                                WhatsApp
                                            </button>
                                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => handleEdit(r.id)}>
                                                Edit
                                            </button>
                                            <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={() => handleDelete(r.id)}>
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {(!rows || rows.length === 0) ? (
                                    <tr>
                                        <td colSpan="6" className="text-center">No receipts</td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default Receipt;
